import AutoLayout from "@src/deprecatedDesignSystem/components/AutoLayout";
import { FC } from "react";
import BuilderActiveClientsStack from "./shared/BuilderActiveClientsStack";
import TranslationDropdown from "./TranslationDropdown";
import { useBuilderContentPermissions } from "@src/components/libraryItemDetailPages/hooks/useBuilderContentPermissions";
import useActionPermissions from "@src/hooks/useActionPermissions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/tooltip";
import {
  PromptTab,
  useLibraryItemQueryParams,
} from "@src/components/libraryItemDetailPages/contexts/LibraryItemQueryParamsContext";
import { Button } from "@ui/button";
import DotsIcon from "@src/ui/icons/18px/dots";
import UsersOutlineIcon from "@src/ui/icons/18px/users-outline";
import PlusIcon from "@src/ui/icons/18px/plus";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@src/ui/dropdown-menu";
import MessageOutlineIcon from "@src/ui/icons/18px/message-outline";

type Props = {
  translationModeEnabled?: boolean;
  setTranslationModeEnabled?: (enabled: boolean) => void;
  onShareClick?: () => void;
  shareDisabled?: boolean;
  hasPublishPermission?: boolean;
  hasCommentPermission?: boolean;
  onPublishClick?: () => void;
  publishDisabled?: boolean;
  publishLoading?: boolean;
  publishButtonText?: string;
  onAssignClick?: () => void;
  assignDisabled?: boolean;
  onPreviewClick?: () => void;
  extraButtons?: React.ReactNode;
  overflowItems?: React.ReactNode;
  selectedMainTab: string;
  hidePublishButton: boolean;
};

const LibraryItemDetailPageHeaderRight: FC<Props> = ({
  translationModeEnabled,
  setTranslationModeEnabled,
  shareDisabled,
  onShareClick,
  hasPublishPermission,
  hasCommentPermission,
  onPublishClick,
  publishDisabled,
  publishLoading,
  publishButtonText = "Publish",
  onAssignClick,
  assignDisabled,
  extraButtons,
  overflowItems,
  selectedMainTab,
  hidePublishButton,
}) => {
  const { userActionPermissions } = useActionPermissions();
  const { hasEditPermission } = useBuilderContentPermissions();
  const { selectedPromptTab, setPromptTab } = useLibraryItemQueryParams();
  return (
    <AutoLayout flex={1}>
      <AutoLayout
        direction={"horizontal"}
        alignSelf={"stretch"}
        spaceBetweenItems={8}
        alignmentVertical={"center"}
        style={{ marginLeft: "auto" }}
      >
        {hasEditPermission && extraButtons}
        <BuilderActiveClientsStack />
        {selectedMainTab === "build" && setTranslationModeEnabled && (
          <TranslationDropdown
            translationModeEnabled={translationModeEnabled}
            setTranslationModeEnabled={setTranslationModeEnabled}
          />
        )}
        {selectedMainTab === "build" && hasCommentPermission && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={
                    selectedPromptTab === PromptTab.Comments
                      ? "default"
                      : "outline"
                  }
                  size="icon"
                  onClick={() => {
                    setPromptTab(
                      selectedPromptTab === PromptTab.Comments
                        ? PromptTab.Design
                        : PromptTab.Comments,
                    );
                  }}
                >
                  <MessageOutlineIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Comments</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hasEditPermission && onShareClick && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={shareDisabled}
                  onClick={onShareClick}
                >
                  <UsersOutlineIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Collaborators</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hasPublishPermission && !hidePublishButton && (
          <Button
            onClick={onPublishClick}
            disabled={publishDisabled || publishLoading}
            dataTestId="publish-button-header"
          >
            {publishButtonText}
          </Button>
        )}
        {userActionPermissions?.assignContent && onAssignClick && (
          <Button
            disabled={assignDisabled}
            dataTestId="assign-course-button"
            onClick={onAssignClick}
          >
            <PlusIcon />
            Assign
          </Button>
        )}
        {hasEditPermission && overflowItems && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <DotsIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>{overflowItems}</DropdownMenuContent>
          </DropdownMenu>
        )}
      </AutoLayout>
    </AutoLayout>
  );
};

export default LibraryItemDetailPageHeaderRight;
